/**
 * The Finance → Withdrawals queue's filter + sort declaration — a consumer of
 * the generic filter toolkit (`components/filters`), mirroring
 * `studentsFilterConfig` / `usersFilterConfig`.
 *
 * Facets exposed:
 *   - status    → `status`                  multi-select over requested/approved/
 *                                            rejected/paid/cancelled. The page's
 *                                            prior default was the "Open"
 *                                            (requested + approved) view, served
 *                                            on the frontend by merging two
 *                                            queries. The backend now defaults an
 *                                            *omitted* status to that same open
 *                                            view, so the default value here is
 *                                            `['requested','approved']` and "All"
 *                                            (no picks) must be sent literally —
 *                                            `allEncoding: 'all'` lifts the
 *                                            constraint to every status.
 *   - method    → `method`                  multi-select over upi_bank/fee_credit/
 *                                            cash; allEncoding 'omit'.
 *   - ownerType → `ownerType`               multi-select over teacher/student;
 *                                            allEncoding 'omit'. Honored only for
 *                                            finance callers (this whole page is
 *                                            finance-gated).
 *   - requested → `requestedFrom`/`requestedTo` date range over the request's
 *                                            `requestedAt` timestamp.
 *
 * NOTE: `academicYear` stays a sibling single-select (NOT a toolkit facet): the
 * backend filters it with a single `eq` over a strict zod string, so a
 * comma-joined multi-select would break it. The route feeds that select's value
 * into the same single query alongside `apiParams`.
 *
 * Sorts map to the backend's allow-listed `sort` fields
 * ({requestedAt, amount, paidAt}). The default (first entry) is "Recently
 * requested" = requestedAt:desc — the page's pre-existing order.
 *
 * Sortable column header: Requested (requestedAt). Both directions are
 * registered so the header can toggle desc↔asc without snapping back to the
 * default sort. amount and paidAt are dropdown-only single-direction sorts.
 */

import { z } from 'zod';
import type { ListFilterConfig, SortColumnBinding } from '../filters';

export const withdrawalsFilterConfig: ListFilterConfig = {
  facets: [
    {
      kind: 'multi-select',
      key: 'status',
      label: 'Status',
      group: 'filters',
      // Prior page default was the "Open" view = requested + approved. The
      // backend now reproduces that for an omitted status, so the default value
      // is those two and "All" (no picks) must emit `status=all` explicitly.
      defaultValue: ['requested', 'approved'],
      allEncoding: 'all',
      options: [
        { value: 'requested', label: 'Requested' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'paid', label: 'Paid' },
        { value: 'cancelled', label: 'Cancelled' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'method',
      label: 'Method',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'upi_bank', label: 'UPI / Bank' },
        { value: 'fee_credit', label: 'Fee credit' },
        { value: 'cash', label: 'Cash' },
      ],
    },
    {
      kind: 'multi-select',
      key: 'ownerType',
      label: 'Owner',
      group: 'filters',
      defaultValue: [],
      allEncoding: 'omit',
      options: [
        { value: 'teacher', label: 'Teachers' },
        { value: 'student', label: 'Students' },
      ],
    },
    {
      kind: 'date-range',
      key: 'requested',
      label: 'Requested',
      group: 'filters',
      params: { from: 'requestedFrom', to: 'requestedTo' },
    },
  ],
  sorts: [
    { value: 'requestedAt:desc', label: 'Recently requested' },
    // Registered so the Requested column header can toggle to ascending; an
    // unregistered `field:dir` would silently fall back to the default sort.
    { value: 'requestedAt:asc', label: 'Oldest first' },
    { value: 'amount:desc', label: 'Amount (high → low)' },
    { value: 'paidAt:desc', label: 'Paid date (newest)' },
  ],
  resultNoun: ['withdrawal', 'withdrawals'],
};

/**
 * Column-header sort bindings, synced to the same `sort` param. Only the
 * Requested column is bound: it sorts on `requestedAt`, the one field with both
 * directions registered above (amount / paidAt stay dropdown-only).
 */
export const WITHDRAWALS_SORT_COLUMNS: Record<'requestedAt', SortColumnBinding> = {
  requestedAt: { field: 'requestedAt', firstClickDir: 'desc' },
};

/**
 * URL search-param contract for the Withdrawals queue, used as the route's
 * `validateSearch`. The filter toolkit is the single source of truth for these
 * values; this schema validates the raw URL at the route boundary (everything
 * optional, since a bare `/finance/withdrawals` opens on the configured
 * defaults).
 *
 * EVERY param key the config can emit must appear here — `z.object().parse`
 * strips unknown keys, so a missing key would be silently dropped on the next
 * read. In particular `requested` is the date-range facet's PRESET key (e.g.
 * `last7`), distinct from the concrete custom bounds `requestedFrom`/
 * `requestedTo`; omitting it would stop a preset choice from sticking.
 * `academicYear` is the sibling single-select (not a toolkit facet) and is
 * declared here too so it round-trips through the route boundary. Kept beside
 * the config (not in the route) so this boundary is unit-testable without
 * loading the route's React/router side effects.
 */
export const withdrawalsSearchSchema = z.object({
  status: z.string().optional(),
  method: z.string().optional(),
  ownerType: z.string().optional(),
  requested: z.string().optional(),
  requestedFrom: z.string().optional(),
  requestedTo: z.string().optional(),
  academicYear: z.string().optional(),
  sort: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
